import { useCallback, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type AdminSystemInfo, type AdminUser, type AiPromptId, type AiSettings, type DashboardScope, type NoteTemplate, type NoteTemplateInput, type Opportunity, type OpportunityDetail, type OpportunityNote, type OppPicklists, type Picklists, type QueryResult, type SalesDashboard, type ServiceDeskDashboard, type Ticket, type TicketDetailResponse, type TicketHistoryEntry, type TimeEntryResult, type WebhookLogEntry } from './client';
import { useScopeFilter } from '@/contexts/ClientScopeContext';

// -------- Picklists (long-lived) --------

export function usePicklists() {
  return useQuery({
    queryKey: ['picklists'],
    queryFn: () => api.get<Picklists>('/api/picklists'),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

// -------- Me --------

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<{ item: { id: number; firstName: string; lastName: string; email: string } | null }>('/api/me'),
    staleTime: 60 * 60 * 1000,
  });
}

export function useAccountProviders(enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ['me-accounts'],
    queryFn: () => api.get<{ providers: string[] }>('/api/me/accounts'),
    staleTime: 60 * 60 * 1000,
  });
}

// -------- Resources (all active) --------

export interface Resource {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
}

export function useResources() {
  return useQuery({
    queryKey: ['resources'],
    queryFn: () => api.get<QueryResult<Resource>>('/api/resources'),
    staleTime: 10 * 60 * 1000,
  });
}

export interface ResourceRole {
  id: number;
  name: string;
}

export function useResourceRoles(resourceId: number | null) {
  return useQuery({
    enabled: resourceId != null,
    queryKey: ['resource-roles', resourceId],
    queryFn: () => api.get<QueryResult<ResourceRole>>(`/api/resources/${resourceId}/roles`),
    staleTime: 5 * 60 * 1000,
  });
}

export interface Contact {
  id: number;
  firstName: string;
  lastName: string;
  emailAddress: string | null;
}

export function useCompanyContacts(companyId: number | null) {
  return useQuery({
    enabled: companyId != null,
    queryKey: ['company-contacts', companyId],
    queryFn: () => api.get<QueryResult<Contact>>(`/api/companies/${companyId}/contacts`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCompanySearch(q: string) {
  return useQuery({
    enabled: q.trim().length >= 2,
    queryKey: ['company-search', q.trim()],
    queryFn: () => api.get<QueryResult<{ id: number; companyName: string }>>(`/api/companies/search?q=${encodeURIComponent(q.trim())}`),
    staleTime: 30_000,
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fields: { companyName: string; companyType: number; companyCategoryID: number; phone: string }) =>
      api.post<{ itemId: number }>('/api/companies', fields),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['company-search'] }); },
  });
}

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fields: Record<string, unknown>) =>
      api.post<{ item: { id: number } }>('/api/tickets', fields),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tickets'] }); },
  });
}

export function useCreateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fields: Record<string, unknown>) =>
      api.post<{ item: { id: number } }>('/api/opportunities', fields),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['opportunities'] }); },
  });
}

// -------- Tickets list --------

export interface TicketFilters {
  assignedTo: 'me' | 'any';
  status: number[];
  excludeStatus: number[];
  queueID: number[];
  priority: number[];
  search: string;
  companyID?: number;
  resourceID?: number;
  createdAfter?: string;
  createdBefore?: string;
  dueBefore?: string;       // overdue drill-down (dueDateTime < value)
  completedAfter?: string;
  completedBefore?: string;
  unassigned?: boolean;
}

/** `options.scoped`: narrow to the header's client unless the caller set `companyID` itself. */
export function useTickets(filtersIn: TicketFilters, options?: { enabled?: boolean; scoped?: boolean }) {
  const { companyId: scopeId } = useScopeFilter();
  const filters = options?.scoped && scopeId != null && !filtersIn.companyID ? { ...filtersIn, companyID: scopeId } : filtersIn;
  const params = new URLSearchParams();
  params.set('assignedTo', filters.assignedTo);
  if (filters.status.length) params.set('status', filters.status.join(','));
  if (filters.excludeStatus.length) params.set('excludeStatus', filters.excludeStatus.join(','));
  if (filters.queueID.length) params.set('queueID', filters.queueID.join(','));
  if (filters.priority.length) params.set('priority', filters.priority.join(','));
  if (filters.search) params.set('search', filters.search);
  if (filters.companyID) params.set('companyID', String(filters.companyID));
  if (filters.resourceID) params.set('resourceID', String(filters.resourceID));
  if (filters.createdAfter) params.set('createdAfter', filters.createdAfter);
  if (filters.createdBefore) params.set('createdBefore', filters.createdBefore);
  if (filters.dueBefore) params.set('dueBefore', filters.dueBefore);
  if (filters.completedAfter) params.set('completedAfter', filters.completedAfter);
  if (filters.completedBefore) params.set('completedBefore', filters.completedBefore);
  if (filters.unassigned) params.set('unassigned', 'true');
  params.set('limit', '200');

  return useQuery({
    enabled: options?.enabled !== false,
    queryKey: ['tickets', filters],
    queryFn: () => api.get<QueryResult<Ticket>>(`/api/tickets?${params.toString()}`),
    refetchInterval: 60_000,
  });
}

// -------- Ticket detail --------

export function useTicket(id: number | undefined) {
  return useQuery({
    enabled: id != null && Number.isFinite(id),
    queryKey: ['ticket', id],
    queryFn: () => api.get<TicketDetailResponse>(`/api/tickets/${id}`),
  });
}

// -------- Contact bulk lookup --------

export function useContactLookup(ids: number[]) {
  const uniq = Array.from(new Set(ids.filter((n) => Number.isFinite(n)))).sort();
  return useQuery({
    enabled: uniq.length > 0,
    queryKey: ['contacts-bulk', uniq],
    queryFn: () =>
      api.get<QueryResult<{ id: number; firstName: string; lastName: string; emailAddress?: string }>>(
        `/api/contacts/lookup-bulk?ids=${uniq.join(',')}`,
      ),
    staleTime: 10 * 60 * 1000,
  });
}

/** Contacts CC'd on a ticket (`TicketAdditionalContacts`). Only fetched when `enabled`. */
export function useTicketAdditionalContacts(ticketId: number | undefined, enabled = true) {
  return useQuery({
    enabled: enabled && ticketId != null && Number.isFinite(ticketId),
    queryKey: ['ticket-additional-contacts', ticketId],
    queryFn: () =>
      api.get<QueryResult<{ id: number; ticketID: number; contactID: number }>>(
        `/api/tickets/${ticketId}/additional-contacts`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

/** Secondary (additional) resources on a ticket (`TicketSecondaryResources`). */
export interface TicketSecondaryResource {
  id: number;
  ticketID: number;
  resourceID: number;
  roleID: number;
}

export function useTicketSecondaryResources(ticketId: number | undefined, enabled = true) {
  return useQuery({
    enabled: enabled && ticketId != null && Number.isFinite(ticketId),
    queryKey: ['ticket-secondary-resources', ticketId],
    queryFn: () =>
      api.get<QueryResult<TicketSecondaryResource>>(`/api/tickets/${ticketId}/secondary-resources`),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Add/remove helpers for the two ticket child collections. Removal takes the LINK row's id
 * (the child record), not the contact/resource id — see the routes for why.
 */
export function useAddAdditionalContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, contactID }: { ticketId: number; contactID: number }) =>
      api.post(`/api/tickets/${ticketId}/additional-contacts`, { contactID }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['ticket-additional-contacts', v.ticketId] }),
  });
}

export function useRemoveAdditionalContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, linkId }: { ticketId: number; linkId: number }) =>
      api.del(`/api/tickets/${ticketId}/additional-contacts/${linkId}`),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['ticket-additional-contacts', v.ticketId] }),
  });
}

export function useAddSecondaryResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, resourceID, roleID }: { ticketId: number; resourceID: number; roleID?: number }) =>
      api.post(`/api/tickets/${ticketId}/secondary-resources`, { resourceID, roleID }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['ticket-secondary-resources', v.ticketId] }),
  });
}

export function useRemoveSecondaryResource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, linkId }: { ticketId: number; linkId: number }) =>
      api.del(`/api/tickets/${ticketId}/secondary-resources/${linkId}`),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: ['ticket-secondary-resources', v.ticketId] }),
  });
}

// -------- Resource bulk lookup (by specific IDs, no domain/license filter) --------

export function useResourceLookup(ids: number[]) {
  const uniq = Array.from(new Set(ids.filter((n) => Number.isFinite(n)))).sort();
  return useQuery({
    enabled: uniq.length > 0,
    queryKey: ['resources-bulk', uniq],
    queryFn: () =>
      api.get<QueryResult<{ id: number; firstName: string; lastName: string }>>(
        `/api/resources/lookup-bulk?ids=${uniq.join(',')}`,
      ),
    staleTime: 10 * 60 * 1000,
  });
}

// -------- Company lookup (for list rendering) --------

export function useCompanyLookup(ids: number[]) {
  const uniq = Array.from(new Set(ids.filter((n) => Number.isFinite(n)))).sort();
  return useQuery({
    enabled: uniq.length > 0,
    queryKey: ['companies', uniq],
    queryFn: () =>
      api.get<QueryResult<{ id: number; companyName: string }>>(
        `/api/companies/lookup?ids=${uniq.join(',')}`,
      ),
    staleTime: 10 * 60 * 1000,
  });
}

// -------- Opportunities --------

export interface OppFilters {
  assignedTo: 'me' | 'any';
  stage: number[];
  status?: number[]; // status ids (0 Not Ready, 1 Active, 2 Lost, 3 Closed, 4 Implemented); omit → active pipeline
  search: string;
  companyID?: number;
  ownerResourceID?: number;
  createdAfter?: string;
  createdBefore?: string;
  closedAfter?: string;
  closedBefore?: string;
  includeAll?: boolean;
}

export function useOppPicklists() {
  return useQuery({
    queryKey: ['opp-picklists'],
    queryFn: () => api.get<OppPicklists>('/api/opportunities/picklists'),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });
}

export function useWonRecentOpportunities(ownerResourceId?: number) {
  const params = ownerResourceId != null ? `?ownerResourceId=${ownerResourceId}` : '';
  return useQuery({
    queryKey: ['opportunities-won-recent', ownerResourceId ?? null],
    queryFn: () => api.get<QueryResult<{ id: number; amount: number | null; closedDate: string | null }>>(`/api/opportunities/won-recent${params}`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useOpportunityNotes(id: number | undefined) {
  return useQuery({
    enabled: id != null && Number.isFinite(id),
    queryKey: ['opportunity-notes', id],
    queryFn: () => api.get<{ items: OpportunityNote[] }>(`/api/opportunities/${id}/notes`),
    staleTime: 60_000,
  });
}

export function useOpportunity(id: number | undefined) {
  return useQuery({
    enabled: id != null && Number.isFinite(id),
    queryKey: ['opportunity', id],
    queryFn: () => api.get<{ item: OpportunityDetail }>(`/api/opportunities/${id}`),
  });
}

/** `options.scoped`: narrow to the header's client unless the caller set `companyID` itself. */
export function useOpportunities(filtersIn: OppFilters, options?: { enabled?: boolean; scoped?: boolean }) {
  const { companyId: scopeId } = useScopeFilter();
  const filters = options?.scoped && scopeId != null && !filtersIn.companyID ? { ...filtersIn, companyID: scopeId } : filtersIn;
  const params = new URLSearchParams();
  params.set('assignedTo', filters.assignedTo);
  if (filters.stage.length) params.set('stage', filters.stage.join(','));
  if (filters.status?.length) params.set('status', filters.status.join(','));
  if (filters.search) params.set('search', filters.search);
  if (filters.companyID) params.set('companyID', String(filters.companyID));
  if (filters.ownerResourceID) params.set('ownerResourceID', String(filters.ownerResourceID));
  if (filters.createdAfter) params.set('createdAfter', filters.createdAfter);
  if (filters.createdBefore) params.set('createdBefore', filters.createdBefore);
  if (filters.closedAfter) params.set('closedAfter', filters.closedAfter);
  if (filters.closedBefore) params.set('closedBefore', filters.closedBefore);
  if (filters.includeAll) params.set('includeAll', 'true');

  // No auto-refresh: opportunities don't change often enough to warrant polling, and
  // the larger inactive views would be heavy on the Autotask rate limit. Data still
  // refetches on mount / window focus and after mutations invalidate the cache.
  return useQuery({
    enabled: options?.enabled !== false,
    queryKey: ['opportunities', filters],
    queryFn: () => api.get<QueryResult<Opportunity>>(`/api/opportunities?${params.toString()}`),
  });
}

// -------- Dashboards --------

export function useServiceDeskDashboard(scope: DashboardScope) {
  return useQuery({
    queryKey: ['dashboard', 'service-desk', scope],
    queryFn: () => api.get<ServiceDeskDashboard>(`/api/dashboards/service-desk?scope=${scope}`),
    staleTime: 60_000,
  });
}

export function useSalesDashboard(scope: DashboardScope) {
  return useQuery({
    queryKey: ['dashboard', 'sales', scope],
    queryFn: () => api.get<SalesDashboard>(`/api/dashboards/sales?scope=${scope}`),
    staleTime: 60_000,
  });
}

// -------- Mutations --------

type OppPatchFields = Record<string, number | string | null | { name: string; value: string }[]>;

// Apply a patch to a cached opportunity. `userDefinedFields` is a *partial* patch
// (one or more UDFs by name) and is merged into the existing array, mirroring how
// Autotask merges UDFs by name — so editing one UDF doesn't drop the others.
function applyOppPatch<T extends Opportunity>(item: T, fields: OppPatchFields): T {
  const { userDefinedFields: udfPatch, ...rest } = fields;
  const next = { ...item, ...rest } as T;
  if (Array.isArray(udfPatch)) {
    const byName = new Map((item.userDefinedFields ?? []).map((f) => [f.name, f]));
    for (const u of udfPatch) byName.set(u.name, u);
    next.userDefinedFields = Array.from(byName.values());
  }
  return next;
}

export function useUpdateOpportunity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fields }: { id: number; fields: OppPatchFields }) =>
      api.patch(`/api/opportunities/${id}`, fields),

    onMutate: async ({ id, fields }) => {
      await qc.cancelQueries({ queryKey: ['opportunity', id] });
      const previousDetail = qc.getQueryData<{ item: OpportunityDetail }>(['opportunity', id]);
      if (previousDetail) {
        qc.setQueryData<{ item: OpportunityDetail }>(['opportunity', id], {
          item: applyOppPatch(previousDetail.item, fields),
        });
      }
      qc.setQueriesData<QueryResult<Opportunity>>({ queryKey: ['opportunities'] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((o) => o.id === id ? applyOppPatch(o, fields) : o),
        };
      });
      return { previousDetail };
    },

    onError: (_err, { id }, context) => {
      if (context?.previousDetail) qc.setQueryData(['opportunity', id], context.previousDetail);
      qc.invalidateQueries({ queryKey: ['opportunities'] });
    },

    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['opportunities'] });
      qc.invalidateQueries({ queryKey: ['opportunity', vars.id] });
    },
  });
}

export function useUpdateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, fields }: { id: number; fields: Record<string, number | string | null> }) =>
      api.patch(`/api/tickets/${id}`, fields),

    onMutate: async ({ id, fields }) => {
      // Cancel any in-flight refetch so it doesn't clobber our optimistic write.
      await qc.cancelQueries({ queryKey: ['ticket', id] });
      const previous = qc.getQueryData<TicketDetailResponse>(['ticket', id]);
      if (previous) {
        qc.setQueryData<TicketDetailResponse>(['ticket', id], {
          ...previous,
          ticket: {
            ...previous.ticket,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            item: { ...(previous.ticket as any).item, ...fields },
          },
        });
      }
      // Update list caches immediately. When a ticket is being assigned (null → non-null),
      // remove it from the list so queue views clear without waiting for the server refetch.
      const isAssigning = 'assignedResourceID' in fields && fields.assignedResourceID != null;
      qc.setQueriesData<QueryResult<Ticket>>({ queryKey: ['tickets'] }, (old) => {
        if (!old) return old;
        return {
          ...old,
          items: isAssigning
            ? old.items.filter((t) => t.id !== id)
            : old.items.map((t) => t.id === id ? { ...t, ...fields } as Ticket : t),
        };
      });
      return { previous };
    },

    onError: (_err, { id }, context) => {
      // Roll back the optimistic update on failure.
      if (context?.previous) {
        qc.setQueryData(['ticket', id], context.previous);
      }
      qc.invalidateQueries({ queryKey: ['tickets'] });
    },

    onSuccess: (_data, vars) => {
      // Re-sync with the server after a successful save.
      qc.invalidateQueries({ queryKey: ['tickets'] });
      qc.invalidateQueries({ queryKey: ['ticket', vars.id] });
    },
  });
}

export interface BulkUpdateResult {
  updated: number;
  failed: number;
  results: { id: number; ok: boolean; error?: string }[];
}

/**
 * Bulk-edit many tickets with the same field values in one request.
 * Invalidates the ticket lists and each affected detail so the grid re-syncs.
 */
export function useBulkUpdateTickets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, fields }: { ids: number[]; fields: Record<string, number | null> }) =>
      api.post<BulkUpdateResult>('/api/tickets/bulk', { ids, fields }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      for (const id of vars.ids) qc.invalidateQueries({ queryKey: ['ticket', id] });
    },
  });
}

// -------- Prefetch helpers --------

export function usePrefetchTicket() {
  const qc = useQueryClient();
  return useCallback((id: number) => {
    qc.prefetchQuery({
      queryKey: ['ticket', id],
      queryFn: () => api.get<TicketDetailResponse>(`/api/tickets/${id}`),
      staleTime: 30_000,
    });
  }, [qc]);
}

// Call once near the top of the authenticated app to warm up long-lived caches
// (picklists, resources) before any page needs them.
export function useEagerPrefetch() {
  const qc = useQueryClient();
  useEffect(() => {
    qc.prefetchQuery({
      queryKey: ['picklists'],
      queryFn: () => api.get<Picklists>('/api/picklists'),
      staleTime: 60 * 60 * 1000,
    });
    qc.prefetchQuery({
      queryKey: ['resources'],
      queryFn: () => api.get<QueryResult<Resource>>('/api/resources'),
      staleTime: 10 * 60 * 1000,
    });
    qc.prefetchQuery({
      queryKey: ['opp-picklists'],
      queryFn: () => api.get<OppPicklists>('/api/opportunities/picklists'),
      staleTime: 60 * 60 * 1000,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function useTicketHistory(id: number | undefined) {
  return useQuery({
    enabled: id != null && Number.isFinite(id),
    queryKey: ['ticket-history', id],
    queryFn: () => api.get<{ items: TicketHistoryEntry[] }>(`/api/tickets/${id}/history`),
    staleTime: 30_000,
  });
}

export function useAddNote() {
  const qc = useQueryClient();
  return useMutation({
    // publish: 1 = All (customer-visible), 2 = Internal. Those are the only two Cosmos writes.
    mutationFn: ({ id, body }: { id: number; body: { title?: string; description: string; noteType?: number; publish?: 1 | 2 } }) =>
      api.post(`/api/tickets/${id}/notes`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['ticket', vars.id] });
    },
  });
}

export function useAddTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: { hoursWorked: number; summaryNotes?: string; internalNotes?: string; dateWorked?: string; startDateTime?: string; endDateTime?: string };
      // The server splits this into a TimeEntry (internalNotes) + a customer-visible note
      // (summaryNotes). `noteError` is set when the time landed but the note didn't.
    }) => api.post<TimeEntryResult>(`/api/tickets/${id}/time`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['ticket', vars.id] });
      // Autotask takes a moment to commit the entry — refetch again after a short delay
      // so the time tab reflects the new entry without requiring a manual refresh.
      setTimeout(() => qc.refetchQueries({ queryKey: ['ticket', vars.id] }), 2000);
    },
  });
}

// -------- Admin --------

export function useIsAdmin() {
  return useQuery({
    queryKey: ['admin', 'check'],
    queryFn: () => api.get<{ isAdmin: boolean }>('/api/admin/check').then(() => true).catch(() => false),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<{ users: AdminUser[] }>('/api/admin/users'),
    staleTime: 30_000,
  });
}

export function useAdminWebhook() {
  return useQuery({
    queryKey: ['admin', 'webhook'],
    queryFn: () => api.get<{ webhook: Record<string, unknown> | null; configured: boolean }>('/api/admin/webhook'),
    staleTime: 60_000,
  });
}

export function useAdminWebhookLogs() {
  return useQuery({
    queryKey: ['admin', 'webhook', 'logs'],
    queryFn: () => api.get<{ logs: WebhookLogEntry[]; total: number }>('/api/admin/webhook/logs'),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useAdminSystem() {
  return useQuery({
    queryKey: ['admin', 'system'],
    queryFn: () => api.get<AdminSystemInfo>('/api/admin/system'),
    staleTime: 10_000,
    refetchInterval: 30_000,
  });
}

export function useAdminAiSettings() {
  return useQuery({
    queryKey: ['admin', 'ai-settings'],
    queryFn: () => api.get<AiSettings>('/api/admin/ai-settings'),
    staleTime: 30_000,
  });
}

export function useSaveAiPrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { prompt: AiPromptId; systemPrompt: string }) =>
      api.patch('/api/admin/ai-settings', vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ai-settings'] }),
  });
}

export function useResetAiPrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prompt: AiPromptId) => api.del(`/api/admin/ai-settings?prompt=${prompt}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ai-settings'] }),
  });
}

// -------- Note templates --------

export function useNoteTemplates() {
  return useQuery({
    queryKey: ['note-templates'],
    queryFn: () => api.get<{ items: NoteTemplate[] }>('/api/note-templates'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateNoteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NoteTemplateInput) => api.post<{ item: NoteTemplate }>('/api/note-templates', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['note-templates'] }),
  });
}

export function useUpdateNoteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: NoteTemplateInput }) =>
      api.patch<{ item: NoteTemplate }>(`/api/note-templates/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['note-templates'] }),
  });
}

export function useDeleteNoteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/api/note-templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['note-templates'] }),
  });
}
